import React, { useEffect, useState } from "react";
import "@/App.css";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/Header";
import StandingsTab from "@/components/StandingsTab";
import ProbabilitiesTab from "@/components/ProbabilitiesTab";
import FixturesTab from "@/components/FixturesTab";
import AdSlot from "@/components/AdSlot";
import { AD_SLOTS, hasAd } from "@/lib/ads";
import { Activity, Sigma, CalendarRange } from "lucide-react";

function App() {
  const [data, setData] = useState({
    standings: null,
    probabilities: null,
    fixtures: null,
    meta: null,
    error: null,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [s, p, f, m] = await Promise.all([
          fetch("/data/standings.json").then((r) => r.json()),
          fetch("/data/probabilities.json").then((r) => r.json()),
          fetch("/data/fixtures.json").then((r) => r.json()),
          fetch("/data/meta.json").then((r) => r.json()),
        ]);
        setData({ standings: s, probabilities: p, fixtures: f, meta: m, error: null });
      } catch (e) {
        setData((d) => ({ ...d, error: String(e) }));
      }
    };
    load();
  }, []);

  if (data.error) {
    return (
      <div className="App p-6 text-sm text-destructive" data-testid="data-error">
        Failed to load data: {data.error}. Run <code>python generate.py</code> to (re)build the JSON files.
      </div>
    );
  }

  if (!data.standings || !data.probabilities || !data.fixtures || !data.meta) {
    return (
      <div className="App min-h-screen flex items-center justify-center" data-testid="loading">
        <div className="text-muted-foreground tracking-widest text-xs uppercase font-display">
          Crunching scenarios…
        </div>
      </div>
    );
  }

  const teamMap = Object.fromEntries(data.standings.teams.map((t) => [t.short, t]));
  const showStickyAd = hasAd("stickyBottom");

  return (
    <div className={`App ${showStickyAd ? "pb-[64px]" : ""}`}>
      <Header meta={data.meta} />

      {hasAd("header") && (
        <div className="max-w-3xl mx-auto px-4 pt-3" data-testid="ad-banner-top-wrap">
          <AdSlot
            slot={AD_SLOTS.header}
            format="auto"
            responsive
            testId="ad-banner-top"
            className="h-[60px]"
          />
        </div>
      )}

      <main className="max-w-3xl mx-auto px-4 pt-4">
        <Tabs defaultValue="probabilities" className="w-full">
          <TabsList
            className="w-full grid grid-cols-3 bg-card border border-border rounded-md p-1 h-12"
            data-testid="main-tabs"
          >
            <TabsTrigger
              value="standings"
              data-testid="tab-standings"
              className="font-display uppercase tracking-wider text-xs sm:text-sm h-full data-[state=active]:bg-secondary data-[state=active]:text-primary text-muted-foreground rounded-sm"
            >
              <Activity className="w-3.5 h-3.5 mr-1.5" />
              Standings
            </TabsTrigger>
            <TabsTrigger
              value="probabilities"
              data-testid="tab-probabilities"
              className="font-display uppercase tracking-wider text-xs sm:text-sm h-full data-[state=active]:bg-secondary data-[state=active]:text-primary text-muted-foreground rounded-sm"
            >
              <Sigma className="w-3.5 h-3.5 mr-1.5" />
              Probabilities
            </TabsTrigger>
            <TabsTrigger
              value="fixtures"
              data-testid="tab-fixtures"
              className="font-display uppercase tracking-wider text-xs sm:text-sm h-full data-[state=active]:bg-secondary data-[state=active]:text-primary text-muted-foreground rounded-sm"
            >
              <CalendarRange className="w-3.5 h-3.5 mr-1.5" />
              Fixtures
            </TabsTrigger>
          </TabsList>

          <TabsContent value="standings" className="mt-4" data-testid="content-standings">
            <StandingsTab standings={data.standings} teamMap={teamMap} />
          </TabsContent>
          <TabsContent value="probabilities" className="mt-4" data-testid="content-probabilities">
            <ProbabilitiesTab
              probabilities={data.probabilities}
              standings={data.standings}
              fixtures={data.fixtures}
              teamMap={teamMap}
            />
          </TabsContent>
          <TabsContent value="fixtures" className="mt-4" data-testid="content-fixtures">
            <FixturesTab fixtures={data.fixtures} teamMap={teamMap} />
          </TabsContent>
        </Tabs>
      </main>

      {showStickyAd && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-[#0A0D0A]"
          data-testid="ad-sticky-bottom-wrap"
        >
          <div className="max-w-3xl mx-auto px-4 py-2">
            <AdSlot
              slot={AD_SLOTS.stickyBottom}
              format="auto"
              responsive
              testId="ad-sticky-bottom"
              className="h-[44px]"
            />
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
}

export default App;
